<template>
  <Transition
    name="notification"
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="transform translate-x-full opacity-0"
    enter-to-class="transform translate-x-0 opacity-100"
    leave-active-class="transition-all duration-200 ease-in"
    leave-from-class="transform translate-x-0 opacity-100"
    leave-to-class="transform translate-x-full opacity-0"
  >
    <div
      v-if="visible"
      class="relative mb-3 rounded-lg shadow-lg overflow-hidden"
      :class="notificationClasses"
    >
      <div class="p-4 pr-12">
        <div class="flex items-start space-x-3 text-red-800">
          <!-- 圖示 -->
          <div class="flex-shrink-0">
            <div 
              class="w-8 h-8 rounded-full flex items-center justify-center"
              :class="iconContainerClasses"
            >
              <i :class="iconClasses"></i>
            </div>
          </div>
          
          <!-- 內容 -->
          <div class="flex-1 min-w-0">
            <h4 v-if="notification.title" class="text-sm font-semibold mb-1">
              {{ notification.title }}
            </h4>
            <p class="text-sm leading-5">
              {{ notification.message }}
            </p>
            
            <!-- 操作按鈕區域 -->
            <div v-if="notification.actions" class="mt-3 flex space-x-2">
              <button
                v-for="action in notification.actions"
                :key="action.key"
                @click="handleAction(action)"
                class="px-3 py-1.5 text-xs font-medium rounded transition-all duration-200"
                :class="getActionClasses(action.type)"
              >
                <i v-if="action.icon" :class="action.icon" class="mr-1"></i>
                {{ action.text }}
              </button>
            </div>
          </div>
        </div>
        
        <!-- 關閉按鈕 -->
        <button
          @click="close"
          class="absolute top-3 right-3 opacity-70 hover:opacity-100 transition-opacity duration-200 p-1 rounded-full hover:bg-black/10"
        >
          <i class="fas fa-times text-xs"></i>
        </button>
      </div>
      
      <!-- 進度條 -->
      <div v-if="notification.autoClose" class="h-1 bg-black/15">
        <div 
          class="h-full transition-all duration-100 ease-linear"
          :class="progressBarClasses"
          :style="{ width: `${progress}%` }"
        ></div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  notification: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['close', 'action'])

const visible = ref(true)
const progress = ref(100)
let timer = null
let progressTimer = null

// 根據類型決定樣式
const notificationClasses = computed(() => {
  const baseClasses = 'border backdrop-blur-sm'
  const typeClasses = {
    success: 'bg-gradient-to-r from-wood-500 to-wood-600 text-white border-wood-400 shadow-wood-soft',
    error: 'bg-gradient-to-r from-red-600 to-red-700 text-white border-red-500 shadow-red-soft',
    warning: 'bg-gradient-to-r from-yellow-600 to-yellow-700 text-wood-100 border-yellow-500 shadow-yellow-soft',
    info: 'bg-gradient-to-r from-wood-400 to-wood-500 text-white border-wood-300 shadow-wood-soft'
  }
  return `${baseClasses} ${typeClasses[props.notification.type] || typeClasses.info}`
})

const iconContainerClasses = computed(() => {
  const typeClasses = {
    success: 'bg-white/25 border border-white/30',
    error: 'bg-white/25 border border-white/30', 
    warning: 'bg-wood-800/20 border border-wood-800/30',
    info: 'bg-white/25 border border-white/30'
  }
  return typeClasses[props.notification.type] || typeClasses.info
})

const iconClasses = computed(() => {
  const typeClasses = {
    success: 'fas fa-check text-white text-sm',
    error: 'fas fa-exclamation-triangle text-white text-sm',
    warning: 'fas fa-exclamation text-wood-800 text-sm',
    info: 'fas fa-info text-white text-sm'
  }
  return typeClasses[props.notification.type] || typeClasses.info
})

const progressBarClasses = computed(() => {
  const typeClasses = {
    success: 'bg-white/70',
    error: 'bg-white/70',
    warning: 'bg-wood-800/60',
    info: 'bg-white/70'
  }
  return typeClasses[props.notification.type] || typeClasses.info
})

// 關閉通知
const close = () => {
  visible.value = false
  clearTimers()
  setTimeout(() => {
    emit('close', props.notification.id)
  }, 250) // 等待動畫結束
}

// 處理操作按鈕點擊
const handleAction = (action) => {
  emit('action', {
    notificationId: props.notification.id,
    actionKey: action.key,
    actionType: action.type
  })
  
  // 如果是關閉類型的操作，也要關閉通知
  if (action.close) {
    close()
  }
}

// 操作按鈕樣式
const getActionClasses = (actionType) => {
  const baseClasses = 'border hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1'
  
  switch (actionType) {
    case 'primary':
      return `${baseClasses} bg-white/90 text-wood-800 border-white/50 hover:bg-white focus:ring-white/50`
    case 'secondary':
      return `${baseClasses} bg-white/20 text-white border-white/30 hover:bg-white/30 focus:ring-white/40`
    default:
      return `${baseClasses} bg-white/20 text-white border-white/30 hover:bg-white/30 focus:ring-white/40`
  }
}

// 清除計時器
const clearTimers = () => {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  if (progressTimer) {
    clearInterval(progressTimer)
    progressTimer = null
  }
}

onMounted(() => {
  if (props.notification.autoClose) {
    const duration = props.notification.duration || 4000
    
    // 設定自動關閉計時器
    timer = setTimeout(() => {
      close()
    }, duration)
    
    // 設定進度條動畫
    const interval = 50 // 每50ms更新一次
    const step = (interval / duration) * 100
    
    progressTimer = setInterval(() => {
      progress.value = Math.max(0, progress.value - step)
      
      if (progress.value <= 0) {
        clearInterval(progressTimer)
        progressTimer = null
      }
    }, interval)
  }
})

onUnmounted(() => {
  clearTimers()
})
</script>

<style scoped>
/* 自訂動畫效果 */
.notification-enter-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.notification-leave-active {
  transition: all 0.2s ease-in;
}

/* 木質調陰影效果 */
.shadow-wood-soft {
  box-shadow: 
    0 4px 6px -1px rgba(166, 139, 91, 0.2),
    0 2px 4px -1px rgba(166, 139, 91, 0.1),
    0 0 0 1px rgba(196, 166, 116, 0.15);
}

.shadow-red-soft {
  box-shadow: 
    0 4px 6px -1px rgba(185, 28, 28, 0.2),
    0 2px 4px -1px rgba(185, 28, 28, 0.1),
    0 0 0 1px rgba(220, 38, 38, 0.15);
}

.shadow-yellow-soft {
  box-shadow: 
    0 4px 6px -1px rgba(217, 119, 6, 0.2),
    0 2px 4px -1px rgba(217, 119, 6, 0.1),
    0 0 0 1px rgba(245, 158, 11, 0.15);
}

/* 木質色彩系統擴展 */
.bg-wood-400 { background-color: #c4a674; }
.bg-wood-500 { background-color: #a68b5b; }
.bg-wood-600 { background-color: #8b7355; }
.border-wood-300 { border-color: #dcc89a; }
.border-wood-400 { border-color: #c4a674; }
.text-wood-100 { color: #f4f0e6; }
.text-wood-800 { color: #4a3f35; }
</style>